/**
 * Controlled-release feature flags.
 *
 * Phase 2 of the consumer experience upgrade replaces the public root with the
 * Discovery Home. The previous landing page is preserved unchanged at
 * `/why-vitolier`, so the swap is fully reversible: turning the flag off
 * restores the old homepage at `/` without any content or SEO loss.
 *
 * Resolution order:
 *   1. explicit local override (`?ff_discovery_home=on|off`, persisted)
 *   2. build-time default
 *
 * Overrides are per-device only and never affect other visitors.
 */

export const FEATURE_FLAGS = {
  /** Discovery Home at `/` (Phase 2). */
  discoveryHome: "discovery_home",
  /** Personalised signed-in Consumer Home at `/app/home` (Phase 3). */
  signedInHome: "signed_in_home",
} as const;

export type FeatureFlag = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

const STORAGE_PREFIX = "vitolier_ff_";

/** Build-time defaults. Flip to false to roll a phase back for everyone. */
const DEFAULTS: Record<FeatureFlag, boolean> = {
  discovery_home: true,
  signed_in_home: true,
};

function storageKey(flag: FeatureFlag) {
  return `${STORAGE_PREFIX}${flag}`;
}

/** Read a per-device override, if one was set. */
export function readFlagOverride(flag: FeatureFlag): boolean | null {
  if (typeof window === "undefined") return null;
  try {
    const value = window.localStorage.getItem(storageKey(flag));
    if (value === "on") return true;
    if (value === "off") return false;
    return null;
  } catch {
    return null;
  }
}

/** Persist (or clear) a per-device override. */
export function setFlagOverride(flag: FeatureFlag, value: boolean | null) {
  if (typeof window === "undefined") return;
  try {
    if (value === null) window.localStorage.removeItem(storageKey(flag));
    else window.localStorage.setItem(storageKey(flag), value ? "on" : "off");
  } catch {
    // ignore
  }
}

/**
 * Apply `?ff_<flag>=on|off` from the current URL, persisting the choice.
 * Called once on app start so support can flip a phase on a single device.
 */
export function applyFlagOverridesFromUrl(search: string) {
  if (!search) return;
  const params = new URLSearchParams(search);
  for (const flag of Object.values(FEATURE_FLAGS)) {
    const raw = params.get(`ff_${flag}`);
    if (raw === "on") setFlagOverride(flag, true);
    else if (raw === "off") setFlagOverride(flag, false);
    else if (raw === "reset") setFlagOverride(flag, null);
  }
}

/** Is a flag enabled for this visitor? */
export function isFeatureEnabled(flag: FeatureFlag): boolean {
  const override = readFlagOverride(flag);
  if (override !== null) return override;
  return DEFAULTS[flag] ?? false;
}
